import type { Expr, FlowDocument } from '@miso/design-system';
import type {
  MovingMethodRequest,
  MovingMethodType,
} from '@/entities/visit-counsel/model/intake.types';
import { MOVING_METHOD_OPTIONS } from './movingMethodOptions';

const Q_METHOD = 'method';
const Q_ENTRANCE_LADDER = 'entranceSpaceLadder';
const Q_ENTRANCE_SKY = 'entranceSpaceSky';
const Q_RAMP = 'ramp';
const Q_STAIR_COUNT = 'stairCount';
const Q_HAS_ELEVATOR = 'hasElevator';

const STEP_TRANSPORT = 'transport';
const TERMINAL = 'saved';

/**
 * 출발지 또는 도착지 운반방식 InquiryFlow 빌더.
 *
 * **단일 step + L2 cascade** — 운반방식 선택 + 분기되는 모든 추가질문이 한 step
 * 안에서 누적된다. 분기는 `skipIf` 로 question 단위로 처리하여 step 마운트가 새로
 * 일어나지 않게 한다.
 *
 * cascade 누적 흐름:
 *   1. Q_METHOD (single-select 5종) — 항상 표시
 *   2. Q_ENTRANCE_LADDER (text)     — Q_METHOD === 'LADDER_TRUCK' 일 때
 *   3. Q_ENTRANCE_SKY    (text)     — Q_METHOD === 'SKY_LIFT'    일 때
 *   4. Q_RAMP            (binary)   — Q_METHOD === 'FIRST_FLOOR' 일 때
 *   5. Q_STAIR_COUNT     (suffix#)  — Q_METHOD === 'FIRST_FLOOR' 일 때
 *   6. Q_HAS_ELEVATOR    (binary)   — ELEVATOR / FIRST_FLOOR 가 아닐 때
 *
 * Figma 노드: 출발지·도착지 모두 같은 분기 패턴(`219:13240`/`225:16062` 등).
 *
 * 이전 6-step 분할 버전은 분기마다 step 가 새로 마운트되어 사용자에게 "각각의
 * Inquiry 가 분리된 듯한" 인상을 주었다. cascade 누적은 동일 step 내부에서만
 * 동작하므로 한 step 으로 모아 자연스러운 cascade UX 를 제공.
 */
export interface BuildMovingMethodFlowOptions {
  /** 'departure' 또는 'arrival' — meta/타이틀 워딩만 다름 */
  site: 'departure' | 'arrival';
}

export function buildMovingMethodFlow({ site }: BuildMovingMethodFlowOptions): FlowDocument {
  const siteLabel = site === 'departure' ? '출발지' : '도착지';
  const titleQuestion = `${siteLabel}에서 어떻게 운반하나요?`;

  const isFirstFloor: Expr = { '==': [{ var: Q_METHOD }, 'FIRST_FLOOR' as MovingMethodType] };
  const notFirstFloor: Expr = { not: isFirstFloor };
  const isElevatorOrFirstFloor: Expr = {
    or: [
      { '==': [{ var: Q_METHOD }, 'ELEVATOR' as MovingMethodType] },
      { '==': [{ var: Q_METHOD }, 'FIRST_FLOOR' as MovingMethodType] },
    ],
  };
  const isLadder: Expr = { '==': [{ var: Q_METHOD }, 'LADDER_TRUCK' as MovingMethodType] };
  const notLadder: Expr = { '!=': [{ var: Q_METHOD }, 'LADDER_TRUCK' as MovingMethodType] };
  const isSky: Expr = { '==': [{ var: Q_METHOD }, 'SKY_LIFT' as MovingMethodType] };
  const notSky: Expr = { '!=': [{ var: Q_METHOD }, 'SKY_LIFT' as MovingMethodType] };
  const notElevatorOrFirstFloor: Expr = { not: isElevatorOrFirstFloor };

  return {
    schemaVersion: 1,
    id: `progress-${site}-transport`,
    initial: STEP_TRANSPORT,
    meta: { appBarTitle: `${siteLabel} 운반방식` },
    steps: {
      [STEP_TRANSPORT]: {
        kind: 'questions',
        questions: [
          {
            id: Q_METHOD,
            // 운반방식 + 모든 후속 분기 질문 모두 L2 cascade 누적.
            layout: 'L2',
            title: titleQuestion,
            ctaLabel: '다음',
            input: {
              kind: 'single-select',
              required: true,
              options: [...MOVING_METHOD_OPTIONS],
            },
          },
          {
            id: Q_ENTRANCE_LADDER,
            layout: 'L2',
            title: '사다리차가 어디로 들어오나요?',
            ctaLabel: '다음',
            skipIf: notLadder,
            input: {
              kind: 'text',
              required: isLadder,
              placeholder: '진입 위치',
              maxLength: 30,
            },
          },
          {
            id: Q_ENTRANCE_SKY,
            layout: 'L2',
            title: '스카이가 어디로 들어오나요?',
            ctaLabel: '다음',
            skipIf: notSky,
            input: {
              kind: 'text',
              required: isSky,
              placeholder: '진입 위치',
              maxLength: 30,
            },
          },
          {
            id: Q_RAMP,
            layout: 'L2',
            title: '경사로가 있나요?',
            ctaLabel: '다음',
            skipIf: notFirstFloor,
            input: {
              kind: 'binary',
              required: isFirstFloor,
              labels: ['있어요', '없어요'],
            },
          },
          {
            id: Q_STAIR_COUNT,
            layout: 'L2',
            title: '계단 수가 몇 개인가요?',
            ctaLabel: '다음',
            skipIf: notFirstFloor,
            input: {
              kind: 'suffix',
              required: isFirstFloor,
              suffix: '개',
              inputType: 'number',
              min: 0,
              step: 1,
              placeholder: '0',
            },
          },
          {
            id: Q_HAS_ELEVATOR,
            layout: 'L2',
            title: '엘리베이터가 있나요?',
            ctaLabel: '다음',
            skipIf: isElevatorOrFirstFloor,
            input: {
              kind: 'binary',
              required: notElevatorOrFirstFloor,
              labels: ['있어요', '없어요'],
            },
          },
        ],
        next: { kind: 'terminal', target: TERMINAL },
      },
    },
    terminals: { [TERMINAL]: { kind: 'placeholder-auto' } },
  };
}

// ── Result mapping ───────────────────────────────────────────────────────────

export interface MovingMethodFlowResult {
  movingMethod: MovingMethodRequest;
  /** 운반방식이 ELEVATOR/FIRST_FLOOR 가 아니면 사용자 답변, 아니면 derived. */
  hasElevator: boolean | null;
}

/**
 * InquiryFlow 답변 → MovingMethodRequest + hasElevator.
 *
 * - 사다리차: attributes.entranceSpace = Q_ENTRANCE_LADDER
 * - 스카이:   attributes.entranceSpace = Q_ENTRANCE_SKY
 * - 계단:        Q_HAS_ELEVATOR 만 저장 (ramp/stairCount 없음)
 * - 1층:         attributes.ramp ('true'|'false'), attributes.stairCount (string number)
 * - ELEVATOR:    hasElevator = true (자동)
 * - FIRST_FLOOR: hasElevator = null / ramp·stairCount 사용자 답변
 * - 그 외:        사용자 답변(`Q_HAS_ELEVATOR`)
 */
export function flowResultToMovingMethod(
  values: Record<string, unknown>
): MovingMethodFlowResult | null {
  const type = values[Q_METHOD];
  if (
    type !== 'ELEVATOR' &&
    type !== 'LADDER_TRUCK' &&
    type !== 'SKY_LIFT' &&
    type !== 'STAIR' &&
    type !== 'FIRST_FLOOR'
  ) {
    return null;
  }
  const attributes: Record<string, string> = {};
  if (type === 'LADDER_TRUCK') {
    const space = values[Q_ENTRANCE_LADDER];
    if (typeof space === 'string' && space.trim() !== '') {
      attributes.entranceSpace = space.trim();
    }
  }
  if (type === 'SKY_LIFT') {
    const space = values[Q_ENTRANCE_SKY];
    if (typeof space === 'string' && space.trim() !== '') {
      attributes.entranceSpace = space.trim();
    }
  }
  if (type === 'FIRST_FLOOR') {
    const ramp = values[Q_RAMP];
    if (typeof ramp === 'boolean') attributes.ramp = ramp ? 'true' : 'false';
    const stairCount = values[Q_STAIR_COUNT];
    if (typeof stairCount === 'string' && stairCount.trim() !== '') {
      const num = parseInt(stairCount, 10);
      if (Number.isFinite(num) && num >= 0) attributes.stairCount = String(num);
    }
  }
  let hasElevator: boolean | null;
  if (type === 'ELEVATOR') hasElevator = true;
  else if (type === 'FIRST_FLOOR') hasElevator = null;
  else {
    const ans = values[Q_HAS_ELEVATOR];
    hasElevator = typeof ans === 'boolean' ? ans : null;
  }
  return {
    movingMethod: {
      type,
      attributes: Object.keys(attributes).length > 0 ? attributes : null,
    },
    hasElevator,
  };
}

/**
 * 저장된 운반방식 → InquiryFlow initialValues (분기 답 포함).
 *
 * 구현은 `@miso/counsel-schema` 에 있다 — 프리뷰도 같은 계산을 쓴다.
 */
export { movingMethodToFlowValues } from '@miso/counsel-schema';
